// Multipart upload that reports progress and can be cancelled. fetch() has no
// upload-progress events, so this uses XMLHttpRequest. Resolves with the
// backend's `data` for the usual { status, message, data } response shape and
// throws Error(message) on failure - same contract as the fetch-based helpers.

export interface UploadProgress {
  loaded: number; // bytes sent so far
  total: number; // total bytes
  percent: number; // 0-100
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled.');
    this.name = 'UploadCancelledError';
  }
}

export function isUploadCancelled(e: unknown): boolean {
  return (
    e instanceof UploadCancelledError || (e as { name?: string })?.name === 'UploadCancelledError'
  );
}

export function postFormWithProgress<T>(
  url: string,
  form: FormData,
  headers: Record<string, string>,
  fallbackError: string,
  options: UploadOptions = {}
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    // Let the browser set the multipart Content-Type (with boundary).
    Object.entries(headers).forEach(([k, v]) => {
      if (k.toLowerCase() !== 'content-type') xhr.setRequestHeader(k, v);
    });

    xhr.upload.onprogress = (e) => {
      if (!options.onProgress || !e.lengthComputable) return;
      options.onProgress({
        loaded: e.loaded,
        total: e.total,
        percent: e.total > 0 ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : 0,
      });
    };

    const onAbort = () => xhr.abort();
    options.signal?.addEventListener('abort', onAbort);
    const cleanup = () => options.signal?.removeEventListener('abort', onAbort);

    xhr.onload = () => {
      cleanup();
      let result: any = {};
      try {
        result = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        result = {};
      }
      if (xhr.status === 413) {
        reject(
          new Error(
            'This file is too large for the server to accept. Documents must be 100 MB or smaller.'
          )
        );
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300 || result?.status === false) {
        reject(new Error(result?.message || fallbackError));
        return;
      }
      resolve((result?.data ?? result) as T);
    };
    xhr.onerror = () => {
      cleanup();
      reject(
        new Error(
          'The upload failed because of a network problem. Please check your connection and try again.'
        )
      );
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(
        new Error(
          'The upload took too long and timed out. Please try again on a faster connection.'
        )
      );
    };
    xhr.onabort = () => {
      cleanup();
      reject(new UploadCancelledError());
    };

    xhr.send(form);
  });
}

// "12.4 MB of 98.0 MB"
export function formatUploadProgress(p: UploadProgress): string {
  const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${mb(p.loaded)} of ${mb(p.total)}`;
}
